<template>
  <div>
    <div class="d-flex align-center mb-6">
      <div>
        <p class="text-2xl mb-0">Data Import</p>
        <p class="text-subtitle-1 mb-0">
          Import Excel data for marketing reports
        </p>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        color="success"
        :loading="processing"
        :disabled="!hasAnyFile"
        class="mr-2"
        @click="processData"
      >
        <v-icon left>{{ icons.mdiCog }}</v-icon>
        Process Data
      </v-btn>
      <v-btn color="primary" :loading="loading" @click="loadData">
        <v-icon left>{{ icons.mdiRefresh }}</v-icon>
        Refresh
      </v-btn>
    </div>

    <v-alert v-if="statusMsg" type="info" dismissible class="mb-4" @input="statusMsg = ''">
      {{ statusMsg }}
    </v-alert>

    <v-card>
      <v-card-title>Import Excel Files</v-card-title>
      <v-card-text>
        <v-row>
          <v-col
            v-for="source in sources"
            :key="source.key"
            cols="12"
            md="6"
          >
            <v-card outlined>
              <v-card-title class="text-subtitle-1">
                {{ source.label }}
              </v-card-title>
              <v-card-text>
                <input
                  type="file"
                  :ref="'file_' + source.key"
                  accept=".xlsx,.xls"
                  style="display: none"
                  @change="handleFileSelect($event, source.key)"
                />
                <v-btn
                  block
                  :loading="loading"
                  @click="triggerFileInput(source.key)"
                >
                  <v-icon left>{{ icons.mdiFileUpload }}</v-icon>
                  {{ excelData[source.key] ? 'Replace File' : 'Import File' }}
                </v-btn>
                <div v-if="excelData[source.key]" class="mt-2 d-flex align-center">
                  <v-chip color="success" small>
                    <v-icon x-small left v-if="serverFiles[source.key]">{{ icons.mdiCloudUpload }}</v-icon>
                    {{ excelData[source.key].fileName }} -
                    {{ excelData[source.key].rowCount }} rows
                  </v-chip>
                  <v-btn
                    icon
                    x-small
                    class="ml-2"
                    color="error"
                    :loading="loading"
                    @click="clearFile(source.key)"
                  >
                    <v-icon small>{{ icons.mdiClose }}</v-icon>
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-alert type="info" dense text class="mt-4">
          <small>
            <strong>Tip:</strong> Uploaded files are saved to the server and will be automatically loaded next time.
            After importing Excel files, click "Process Data" to extract labels and generate reports.
          </small>
        </v-alert>

        <v-divider class="my-6"></v-divider>

        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header>Debug Info</v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="mb-2">
                <strong>Master Brands:</strong> {{ masterBrands.length }}
              </div>
              <div class="mb-2">
                <strong>Matching Rules:</strong> {{ matchingRules.length }}
              </div>
              <div class="mb-2">
                <strong>Brand Reports:</strong> {{ brandReports.length }}
              </div>
              <v-btn small color="info" class="mt-2" @click="debugData">
                Print Full Debug to Console
              </v-btn>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import {
  mdiCog,
  mdiRefresh,
  mdiFileUpload,
  mdiClose,
  mdiCloudUpload,
  mdiCloudDownload,
} from "@mdi/js";
import * as XLSX from "xlsx";
import axios from "@axios";

export default {
  name: "DataImport",
  data() {
    return {
      loading: false,
      processing: false,
      statusMsg: "",
      icons: {
        mdiCog,
        mdiRefresh,
        mdiFileUpload,
        mdiClose,
        mdiCloudUpload,
        mdiCloudDownload,
      },
      serverFiles: {},
      sources: [
        { key: "audienceBrand", label: "Audience Brand" },
        { key: "audiencePrint", label: "Audience Print" },
        { key: "audienceDigit", label: "Audience Digit" },
        { key: "diffPrint", label: "Diffusion Print" },
        { key: "social", label: "Social" },
        { key: "audio", label: "Audio" },
        { key: "newsletter", label: "Newsletter" },
      ],
      excelData: {
        audienceBrand: null,
        audiencePrint: null,
        audienceDigit: null,
        diffPrint: null,
        social: null,
        audio: null,
        newsletter: null,
      },
      columnMapping: {
        audienceBrand: {
          headerRow: 0,
          dataStartRow: 1,
          labelColumn: 0,
          valueColumns: [2],
        },
        audiencePrint: {
          headerRow: 0,
          dataStartRow: 1,
          labelColumn: 1,
          valueColumns: [2],
        },
        audienceDigit: {
          headerRow: 0,
          dataStartRow: 2,
          labelColumn: 0,
          valueColumns: [1, 2, 3],
        },
        diffPrint: {
          headerRow: -1,
          dataStartRow: 0,
          labelColumn: 0,
          valueColumns: [1],
        },
        social: {
          headerRow: 0,
          dataStartRow: 1,
          labelColumn: 2,
          valueColumns: [5],
        },
        audio: {
          headerRow: 0,
          dataStartRow: 1,
          labelColumn: 0,
          valueColumns: [2],
        },
        newsletter: {
          headerRow: -1,
          dataStartRow: 0,
          labelColumn: 0,
          valueColumns: [1],
        },
      },
      masterBrands: [],
      matchingRules: [],
      brandReports: [],
    };
  },
  computed: {
    hasAnyFile() {
      return Object.values(this.excelData).some(function(d) {
        return d !== null;
      });
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      var self = this;
      self.loading = true;
      try {
        var response = await axios.get("/api/marketing/excel-files");
        var files = response.data.files || [];

        self.serverFiles = {};
        files.forEach(function(file) {
          self.serverFiles[file.source_key] = file.exists;
        });

        var existingFiles = files.filter(function(f) { return f.exists; });
        if (existingFiles.length > 0) {
          self.statusMsg = "Loading " + existingFiles.length + " saved file(s) from server...";
          for (var i = 0; i < existingFiles.length; i++) {
            var file = existingFiles[i];
            await self.loadFileFromServer(file.source_key);
          }
          self.statusMsg = "Loaded " + existingFiles.length + " saved file(s) from server";
        } else {
          self.statusMsg = "Ready to import Excel files";
        }
      } catch (error) {
        console.error("Error loading data:", error);
        self.statusMsg = "Error: " + error.message;
      } finally {
        self.loading = false;
      }
    },
    async loadFileFromServer(sourceKey) {
      var self = this;
      try {
        var response = await axios.get("/api/marketing/excel-files/" + sourceKey, {
          responseType: "arraybuffer",
        });

        var data = new Uint8Array(response.data);
        var workbook = XLSX.read(data, { type: "array" });
        var firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        var jsonData = XLSX.utils.sheet_to_json(firstSheet, {
          header: 1,
          defval: null,
        });

        self.excelData[sourceKey] = {
          fileName: sourceKey + ".xlsx",
          data: jsonData,
          rowCount: jsonData.length,
          fromServer: true,
        };

        console.log(sourceKey + " loaded from server:", jsonData.slice(0, 5));
      } catch (error) {
        console.error("Error loading file from server for " + sourceKey + ":", error);
      }
    },
    triggerFileInput(sourceKey) {
      var refName = "file_" + sourceKey;
      var refs = this.$refs[refName];
      if (refs && refs[0]) {
        refs[0].click();
      }
    },
    async handleFileSelect(event, sourceKey) {
      var self = this;
      var file = event.target.files[0];
      if (!file) return;

      self.loading = true;
      self.statusMsg = "Uploading " + file.name + " to server...";

      try {
        var formData = new FormData();
        formData.append("file", file);

        await axios.post("/api/marketing/excel-files/" + sourceKey, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        self.serverFiles[sourceKey] = true;

        var arrayBuffer = await file.arrayBuffer();
        var data = new Uint8Array(arrayBuffer);
        var workbook = XLSX.read(data, { type: "array" });
        var firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        var jsonData = XLSX.utils.sheet_to_json(firstSheet, {
          header: 1,
          defval: null,
        });

        self.excelData[sourceKey] = {
          fileName: sourceKey + ".xlsx",
          data: jsonData,
          rowCount: jsonData.length,
          fromServer: true,
        };

        self.statusMsg = "Uploaded and loaded " + jsonData.length + " rows from " + sourceKey;
        console.log(sourceKey + " data:", jsonData.slice(0, 5));
      } catch (error) {
        console.error("Error uploading/parsing Excel file:", error);
        self.statusMsg = "Error: " + error.message;
      } finally {
        self.loading = false;
        var refName = "file_" + sourceKey;
        var refs = self.$refs[refName];
        if (refs && refs[0]) {
          refs[0].value = "";
        }
      }
    },
    async clearFile(sourceKey) {
      var self = this;
      self.loading = true;
      try {
        if (self.serverFiles[sourceKey]) {
          await axios.delete("/api/marketing/excel-files/" + sourceKey);
          self.serverFiles[sourceKey] = false;
          self.statusMsg = "File " + sourceKey + ".xlsx deleted from server";
        }
        self.excelData[sourceKey] = null;
        var refName = "file_" + sourceKey;
        var refs = self.$refs[refName];
        if (refs && refs[0]) {
          refs[0].value = "";
        }
      } catch (error) {
        console.error("Error deleting file:", error);
        self.statusMsg = "Error: " + error.message;
      } finally {
        self.loading = false;
      }
    },
    cleanLabel(label, source) {
      if (!label) return label;

      var labelStr = String(label);

      if (source === "audienceBrand" || source === "audiencePrint") {
        var pattern = /^Brand\s*-\s*\d+\s*jours\s*/i;
        labelStr = labelStr.replace(pattern, "");
      }

      labelStr = labelStr.replace(/\s*\([^)]*\)/g, "");

      return labelStr.trim();
    },
    excelSerialToFrenchMonth(value) {
      if (typeof value === "number") {
        var excelEpoch = new Date(1899, 11, 30);
        var date = new Date(excelEpoch.getTime() + value * 86400000);

        var monthNames = [
          "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
          "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
        ];

        var month = monthNames[date.getMonth()];
        var year = date.getFullYear();

        return month + " " + year;
      }

      if (typeof value === "string") {
        var monthMap = {
          "janv": "Janvier", "jan": "Janvier",
          "févr": "Février", "fév": "Février", "feb": "Février",
          "mars": "Mars", "mar": "Mars",
          "avr": "Avril", "avril": "Avril",
          "mai": "Mai",
          "juin": "Juin",
          "juil": "Juillet", "juill": "Juillet",
          "août": "Août", "aout": "Août",
          "sept": "Septembre", "sep": "Septembre",
          "oct": "Octobre",
          "nov": "Novembre",
          "déc": "Décembre", "dec": "Décembre"
        };

        var match = value.toLowerCase().match(/([a-zéû]+)\.?\s*-?\s*(\d{2,4})/);
        if (match) {
          var monthAbbr = match[1];
          var yearStr = match[2];

          if (yearStr.length === 2) {
            yearStr = "20" + yearStr;
          }

          for (var key in monthMap) {
            if (monthAbbr.indexOf(key) !== -1 || key.indexOf(monthAbbr) !== -1) {
              return monthMap[key] + " " + yearStr;
            }
          }
        }
      }

      return String(value);
    },
    async processData() {
      var self = this;
      self.processing = true;
      self.statusMsg = "Processing Excel data...";

      try {
        var labelsBySource = {};

        Object.keys(self.excelData).forEach(function(source) {
          var data = self.excelData[source];
          if (!data) return;

          var config = self.columnMapping[source];
          if (!config) return;

          console.log(
            "Extracting labels from " + source +
            " (labelColumn: " + config.labelColumn +
            ", startRow: " + config.dataStartRow + ")"
          );

          var labelsSet = {};

          for (var i = config.dataStartRow; i < data.data.length; i++) {
            var row = data.data[i];
            if (!row) continue;

            var originalLabel = row[config.labelColumn];
            if (!originalLabel || originalLabel === "" || originalLabel === "-") continue;

            var cleanedLabel = self.cleanLabel(originalLabel, source);

            var labelKey = JSON.stringify({
              original: String(originalLabel),
              cleaned: cleanedLabel,
            });
            labelsSet[labelKey] = true;
          }

          labelsBySource[source] = Object.keys(labelsSet).map(function(l) {
            return JSON.parse(l);
          });
        });

        console.log("Extracted labels by source:", labelsBySource);

        var rules = [];
        Object.keys(labelsBySource).forEach(function(source) {
          labelsBySource[source].forEach(function(labelInfo) {
            rules.push({
              source: source,
              excel_label: labelInfo.original,
              cleaned_label: labelInfo.cleaned,
            });
          });
        });

        if (rules.length > 0) {
          self.statusMsg = "Saving " + rules.length + " matching rules...";
          var response = await axios.post("/api/marketing/matching-rules/bulk", rules);
          var result = response.data;
          self.statusMsg = "Created " + result.created + " new rules, " + result.skipped + " already existed. Processing data...";

          var excelRows = self.buildExcelRows();
          if (excelRows.length > 0) {
            var processResponse = await axios.post("/api/marketing/brand-reports/process", {
              rows: excelRows,
            });
            self.statusMsg = "Created " + result.created + " new rules, " + result.skipped + " already existed. " + processResponse.data.message;
          }
        } else {
          self.statusMsg = "No labels found to process";
        }

      } catch (error) {
        console.error("Error processing Excel data:", error);
        self.statusMsg = "Error: " + error.message;
      } finally {
        self.processing = false;
      }
    },
    buildExcelRows() {
      var self = this;
      var rows = [];

      Object.keys(self.excelData).forEach(function(source) {
        var data = self.excelData[source];
        if (!data) return;

        var config = self.columnMapping[source];
        if (!config) return;

        var headerRow = config.headerRow >= 0 ? data.data[config.headerRow] : null;

        for (var i = config.dataStartRow; i < data.data.length; i++) {
          var row = data.data[i];
          if (!row) continue;

          var originalLabel = row[config.labelColumn];
          if (!originalLabel || originalLabel === "" || originalLabel === "-") continue;

          var values = config.valueColumns.map(function(colIdx) {
            var val = row[colIdx];
            if (val === null || val === undefined || val === "" || val === "ND" || val === "-") {
              return null;
            }
            return typeof val === "number" ? val : parseFloat(val) || null;
          });

          var headerValues = null;
          if (headerRow && source === "audienceDigit") {
            headerValues = config.valueColumns.map(function(colIdx) {
              var headerVal = headerRow[colIdx];
              if (headerVal) {
                return self.excelSerialToFrenchMonth(headerVal);
              }
              return null;
            });
          }

          rows.push({
            source: source,
            excel_label: String(originalLabel),
            values: values,
            header_values: headerValues,
          });
        }
      });

      return rows;
    },
    debugData() {
      console.log("=== FULL DEBUG DUMP ===");
      console.log("Excel Data:", this.excelData);
      console.log("Master Brands:", this.masterBrands);
      console.log("Matching Rules:", this.matchingRules);
      console.log("Brand Reports:", this.brandReports);
    },
  },
};
</script>
